import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeGoLiveAction, DTypeGoLiveActionEnum, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, firstValueFrom, map, Subject, throwError } from 'rxjs';
import { DTypeService } from '../dtype.service';
import { QuarantineColumnComparison, QuarantineColumnComparisonResult, QuarantineDrawModel, QuarantineItemComparison, QuarantineRequestToGoLive, QuarantineStatus, QuarantineTableComparison, QuarantineTableComparisonResult, QuarantineTableInfo, QuarantineTableObject, QuarantineTableWhyThisTable, DTypeGoLiveActionButton, DTypeGoLiveActionButtonContent, DTypeToGoLiveScheduleBody } from '../dtype.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { FuseConfirmationConfig } from '../../../../@fuse/services/confirmation/confirmation.types';
import { RequestGoLiveDialogData } from '../../../dialogs/request-go-live-dialog/request-go-live-dialog.types';
import { USER_URI } from '../../../modules/admin/projects/projects.service';

@Component({
    selector: 'dtype-quarantine',
    templateUrl: 'dtype-quarantine.component.html',
    styles: [
        /* language=SCSS */
        `
        .mat-divider{
            border-top-width: 2px;
        }
        .mat-tooltip {
            font-size: 16px !important;
        }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeQuarantineComponent implements OnInit, OnDestroy {

    @Input() uri: string;
    @Input() dtypeLocator: NextLocator;
    @Output() readonly revertView = new EventEmitter<void>();

    loading: boolean;
    data: QuarantineRequestToGoLive;
    drawQuarantineTables: QuarantineDrawModel[] = [];
    greatestSize: number = 0;
    showView: string;

    viewMode: FormControl;

    visibleDesign: boolean = false;
    visibleLive: boolean = false;
    visibleCompare: boolean = false;

    requestToGoLiveResume: string;

    private unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private dTypeService: DTypeService,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private translocoService: TranslocoService,
    ) { }

    async ngOnInit() {
        this.loading = true;
        this.viewMode = new FormControl('');

        this.data = await firstValueFrom(this.dTypeService.retrieveDTypeGoLiveRequest(this.uri));
        if (this.data) {
            for (const comparisonResult of this.data?.tableComparision) {
                comparisonResult.columnComparision?.sort((a, b) => a.fieldName <= b.fieldName ? -1 : 1);
                const _design = this.generateDrawTable(comparisonResult.design, comparisonResult.whyThisTable, comparisonResult.result, comparisonResult.columnComparision,
                    QuarantineStatus.DESIGN);
                const _live = this.generateDrawTable(comparisonResult.live, comparisonResult.whyThisTable, comparisonResult.result, comparisonResult.columnComparision,
                    QuarantineStatus.LIVE);
                const _bestCompatible = this.generateDrawTable(comparisonResult.bestCompatible, comparisonResult.whyThisTable, comparisonResult.result,
                    comparisonResult.columnComparision, QuarantineStatus.BEST_COMPATIBLE);
                let _title: string = this.translocoService.translate('dbTables.table') + ' ' + comparisonResult.whyThisTable?.type + ' --> '
                    + this.translocoService.translate('dbTables.type') + ' ' + comparisonResult?.whyThisTable?.facadeUri.split('.').slice(-1)[0]
                    + ' --> ' + comparisonResult.whyThisTable?.uriName;
                if (comparisonResult.whyThisTable?.type === 'DERIVED') {
                    _title = _title  + '[' + comparisonResult.whyThisTable?.field.join() + ']';
                }
                this.calculateIndexDifferences(_design, _live, _bestCompatible);

                const _buttons = this.configureButtons(comparisonResult.uri === 'main' ? comparisonResult.whyThisTable.uri : comparisonResult.uri, comparisonResult.result);

                this.drawQuarantineTables.push({
                    design: QuarantineTableComparisonResult.MISSING_DESIGN === comparisonResult.result ? null : _design,
                    live: QuarantineTableComparisonResult.MISSING_LIVE === comparisonResult.result ? null : _live,
                    bestCompatible: QuarantineTableComparisonResult.COMPATIBLE === comparisonResult.result ||
                        QuarantineTableComparisonResult.INCOMPATIBLE === comparisonResult.result ? _bestCompatible : null,
                    tableComparison: comparisonResult.result,
                    title: _title,
                    buttons: _buttons
                });
            }
            this.greatestSize = this.drawQuarantineTables.length;
        } else {
            const config: FuseConfirmationConfig = {
                title: this.translocoService.translate('dbTables.requestNotFound.title'),
                message: this.translocoService.translate('dbTables.requestNotFound.message'),
                actions: {
                    confirm: {
                        label: this.translocoService.translate('accept')
                    },
                    cancel: {
                        show: false
                    }
                }
            };
            this._fuseConfirmationService.open(config).afterClosed().subscribe(() => {
                this.loading = false;
                this.revertView.emit();
            });
        }
        this.loading = false;
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    showRequest() {
        const data: RequestGoLiveDialogData = {
            dtypeToGoLiveRequest: this.data.dtypeToGoLiveRequest,
            userName: this.data.dtypeToGoLiveRequest.userId ? this.data.partaker[USER_URI][this.data.dtypeToGoLiveRequest.userId].displayName : undefined,
        };
        this.dialogsService.openRequestGoLiveDataDialog(data);
    }

    async reject() {
        this.loading = true;
        const confirm = await this.openConfirmDialogWithCountdown(
            this.translocoService.translate('quarantineReject.title'),
            this.translocoService.translate('quarantineReject.message'),
            this.translocoService.translate('quarantineReject.countdownMessage'));
        if (confirm) {
            const body: DTypeToGoLiveScheduleBody = {
                uri: this.uri,
                rejected: true
            };
            this.dTypeService.flushDTypeToGoLiveSchedule(body).pipe(
                catchError((error) => {
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.revertView.emit();
            });
        }
        this.loading = false;
    }

    async accept() {
        this.loading = true;
        const actions: DTypeGoLiveAction[] = [];
        for (const comparison of this.drawQuarantineTables) {
            if (QuarantineTableComparisonResult.MISSING_DESIGN === comparison.tableComparison) {
                actions.push({uri: comparison.buttons.uri, action: comparison.buttons.design.selected ? DTypeGoLiveActionEnum.DELETE : DTypeGoLiveActionEnum.PRESERVE});
            } else if (QuarantineTableComparisonResult.MISSING_LIVE !== comparison.tableComparison && QuarantineTableComparisonResult.EQUAL !== comparison.tableComparison) {
                if (comparison.buttons.migrate.selected) {
                    actions.push({uri: comparison.buttons.uri,
                        action: comparison.buttons.design.selected ? DTypeGoLiveActionEnum.DESIGN : DTypeGoLiveActionEnum.COMPATIBLE});
                } else {
                    actions.push({uri: comparison.buttons.uri,
                        action: comparison.buttons.design.selected ? DTypeGoLiveActionEnum.DESIGN_NO_MIGRATE : DTypeGoLiveActionEnum.COMPATIBLE_NO_MIGRATE});
                }
            }
        }
        const body: DTypeToGoLiveScheduleBody = {
            uri: this.uri,
            dtypeToGoLiveSchedule: {
                actions: actions
            }
        };

        this.dialogsService.openScheduleGoLiveDataDialog(body).afterClosed().subscribe(async (schedule) => {
            if (schedule) {
                const confirm2 = await this.openConfirmDialogWithCountdown(
                    this.translocoService.translate('quarantineAccept.title'),
                    this.translocoService.translate('quarantineAccept.message'),
                    this.translocoService.translate('quarantineAccept.countdownMessage'));
                if (confirm2) {
                    this.dTypeService.flushDTypeToGoLiveSchedule(schedule).pipe(
                        catchError((error) => {
                            console.error(error);
                            return throwError(() => error.error);
                        }),
                        map(reply => reply.repository)
                    ).subscribe(async (repo) => {
                        this.revertView.emit();
                    });
                }
            }
        });
        this.loading = false;
    }

    checkButton(index: number, toCheck: string) {
        if (toCheck === 'design' && !this.drawQuarantineTables[index].buttons.design.selected) {
            this.drawQuarantineTables[index].buttons.design.selected = true;
            this.drawQuarantineTables[index].buttons.compatible.selected = false;
        }
        if (toCheck === 'compatible' && !this.drawQuarantineTables[index].buttons.compatible.selected) {
            this.drawQuarantineTables[index].buttons.design.selected = false;
            this.drawQuarantineTables[index].buttons.compatible.selected = true;
        }
    }

    private generateDrawTable(table: QuarantineTableInfo, generalInfo: QuarantineTableWhyThisTable,
        result: QuarantineTableComparisonResult, columnComparison: QuarantineColumnComparison[], status: QuarantineStatus): QuarantineTableComparison {
        if (!table) {
            return null;
        }

        const _tableType: QuarantineItemComparison = table?.whyThisTable?.type ? {
            name: 'dbTables.tableType',
            value: table?.whyThisTable?.type,
            isDifferent: table?.whyThisTable?.type !== generalInfo.type,
            onlyHere: false
        } : undefined;
        const _elementType: QuarantineItemComparison = table.whyThisTable?.facadeUri ? {
            name: 'dbTables.elementType',
            value: table?.whyThisTable?.facadeUri.split('.').slice(-1)[0],
            isDifferent: table?.whyThisTable?.facadeUri.split('.').slice(-1)[0] !== generalInfo?.facadeUri.split('.').slice(-1)[0],
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.facadeUri, show: false}
        } : undefined;
        const _tableName: QuarantineItemComparison = {
            name: 'dbTables.tableName',
            value: table.name ?? '<<' + this.translocoService.translate('dbTables.unassigned') + '>>',
            isDifferent: false,
            onlyHere: false,
            tooltipItem: table.name ? {tooltip: '', show: true} : undefined
        };
        const _tableSchema: QuarantineItemComparison = {
            name: 'dbTables.tableSchema',
            value: table.schema ?? '<<' + this.translocoService.translate('dbTables.unassigned') + '>>',
            isDifferent: false, onlyHere: false
        };
        const _uriName: QuarantineItemComparison = table.whyThisTable?.uriName ? {
            name: 'dbTables.uriName',
            value: table.whyThisTable?.uriName,
            isDifferent: table.whyThisTable?.uriName !== generalInfo.uriName,
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.uri, show: false}
        } : undefined;
        const _linkField: QuarantineItemComparison = table.whyThisTable?.field?.length > 0 ? {
            name: 'dbTables.linkField',
            value: table.whyThisTable?.field.join(),
            isDifferent: table.whyThisTable?.field.join() !== generalInfo?.field.join(),
            onlyHere: false
        } : undefined;
        const _linkType: QuarantineItemComparison = table.whyThisTable?.complexName ? {
            name: 'dbTables.linkType',
            value: table.whyThisTable?.complexName,
            isDifferent: table.whyThisTable?.complexName !== generalInfo?.complexName,
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.complexUri, show: false}
        } : undefined;

        const _dn: QuarantineItemComparison[] = [];
        if (table.whyThisTable?.dn?.parts?.length > 0) {
            for (const item of table.whyThisTable?.dn?.parts) {
                _dn.push({name: item.name, value: item.text, isDifferent: false, onlyHere: false});
            }
        }

        const _columns: QuarantineItemComparison[] = [];
        if (columnComparison?.length > 0) {
            for (const columnComparisonResult of columnComparison) {
                const item = table.columns.filter(_item => _item.name === columnComparisonResult.fieldName)[0];
                if (!item) {
                    _columns.push({name: '', value: '', isDifferent: false, onlyHere: true, tooltipItem: {tooltip: 'name: ' + columnComparisonResult.fieldName, show: false}});
                } else {
                    let _value: string;
                    if (item.length !== -1) {
                        if (item.scale > 0) {
                            _value = item.sqlType + '(' + item.length + ',' + item.scale + ')';
                        } else {
                            _value = item.sqlType + '(' + item.length + ')';
                        }
                    } else {
                        _value = item.sqlType;
                    }
                    const _tooltip: string =
                        'name: ' + item.name + '\n' +
                        'ordinal: ' + item.ordinal + '\n' +
                        'sqlType: ' + item.sqlType + '\n' +
                        'type: ' + item.type + '\n' +
                        'length: ' + item.length + '\n' +
                        'scale: ' + item.scale + '\n' +
                        'nullable: ' + item.nullable + '\n';

                    let _onlyHere: boolean = false;
                    let _isDifferent: boolean = false;

                    if (columnComparisonResult.fieldName === item.name) {
                        switch(columnComparisonResult.result) {
                            case QuarantineColumnComparisonResult.BACKWARD_COMPATIBLE:
                            case QuarantineColumnComparisonResult.FORWARD_COMPATIBLE:
                            case QuarantineColumnComparisonResult.INCOMPATIBLE:
                                _isDifferent = true;
                                _onlyHere = status === QuarantineStatus.BEST_COMPATIBLE;
                                break;
                            case QuarantineColumnComparisonResult.MISSING_DESIGN:
                                _isDifferent = status === QuarantineStatus.BEST_COMPATIBLE;
                                _onlyHere = status !== QuarantineStatus.DESIGN;
                                break;
                            case QuarantineColumnComparisonResult.MISSING_LIVE:
                                _isDifferent = status === QuarantineStatus.BEST_COMPATIBLE;
                                _onlyHere = status !== QuarantineStatus.LIVE;
                                break;
                            case QuarantineColumnComparisonResult.EQUAL:
                            default:
                                _isDifferent = false;
                                _onlyHere = false;
                                break;
                        }
                    }
                    _columns.push({name: item.name, value: _value, isDifferent: _isDifferent, onlyHere: _onlyHere, tooltipItem: {tooltip: _tooltip, show: false}});
                }
            }
        }

        const _indexes: QuarantineItemComparison[] = [];
        if (table.indexes?.length > 0) {
            for (const _index of table.indexes) {
                let _indexValue: string = '';
                const _indexValues: string[] = [];
                let _indexName: string = '';
                for (const _column of _index.columns) {
                    if (_indexValue === '') {
                        _indexValue = _column.columnName;
                        if (_column.descending) {
                            _indexValue = _indexValue + ' DESC';
                            _indexValues.push(_indexValue);
                        } else {
                            _indexValues.push(_indexValue);
                        }
                    } else {
                        _indexValue += ', ' + _column.columnName;
                        if (_column.descending) {
                            _indexValue = _indexValue + ' DESC';
                            _indexValues.push(_indexValue);
                        } else {
                            _indexValues.push(_indexValue);
                        }
                    }
                    if (_index.primary) {
                        _indexName = 'primary';
                    } else if (_index.unique) {
                        _indexName = 'unique';
                    } else {
                        _indexName = 'index';
                    }
                }
                _indexes.push({name: _indexName, value: _indexValue, listValues: _indexValues, isDifferent: false, onlyHere: false});
            }
        }

        const drawTable: QuarantineTableComparison = {
            title: status === QuarantineStatus.BEST_COMPATIBLE ? (result === QuarantineTableComparisonResult.INCOMPATIBLE ? 'BEST INCOMPATIBLE' : 'BEST COMPATIBLE') : status,
            tableType: _tableType,
            tableName: _tableName,
            tableSchema: _tableSchema,
            elementType: _elementType,
            uriName: _uriName,
            linkField: _linkField,
            linkType: _linkType,
            dn: _dn,
            columns: _columns,
            indexes: _indexes,
            status: status,
        };
        return drawTable;
    }

    private calculateIndexDifferences(design: QuarantineTableComparison, live: QuarantineTableComparison, bestCompatible: QuarantineTableComparison) {
        if (design && live) {
            //Items table1
            for (const _originItem of design.indexes) {
                let _exists = false;
                let _equal = true;
                for (const _compareItem of live.indexes) {
                    if (_originItem.name === _compareItem.name) {
                        _exists = true;
                        if (_originItem.listValues?.length !== _compareItem.listValues?.length) {
                            _originItem.isDifferent = true;
                            _compareItem.isDifferent = true;
                        } else {
                            for (const _originValue of _originItem.listValues) {
                                let _valueExists = false;
                                for (const _compareValue of _compareItem.listValues) {
                                    if (_originValue === _compareValue) {
                                        _valueExists = true;
                                    }
                                }
                                if (!_valueExists) {
                                    _equal = false;
                                }
                            }
                            if (!_equal) {
                                _originItem.isDifferent = true;
                                _compareItem.isDifferent = true;
                            }
                        }
                    }
                }
                if (!_exists) {
                    _originItem.onlyHere = true;
                }
                if (bestCompatible && (_originItem.isDifferent || _originItem.onlyHere)) {
                    for (const _compatibleItem of bestCompatible.indexes) {
                        if (_originItem.name === _compatibleItem.name) {
                            _compatibleItem.isDifferent = true;
                            _compatibleItem.onlyHere = true;
                        }
                    }
                }
            }
            //Items table2
            for (const _originItem of live.indexes) {
                let _exists = false;
                for (const _compareItem of design.indexes) {
                    if (_originItem.name === _compareItem.name) {
                        _exists = true;
                    }
                }
                if (!_exists) {
                    _originItem.onlyHere = true;
                }
                if (bestCompatible && _originItem.onlyHere) {
                    for (const _compatibleItem of bestCompatible.indexes) {
                        if (_originItem.name === _compatibleItem.name) {
                            _compatibleItem.isDifferent = true;
                            _compatibleItem.onlyHere = true;
                        }
                    }
                }
            }
        } else if (!design) {
            for (const _originItem of live.indexes) {
                _originItem.onlyHere = false;
            }
            if (bestCompatible) {
                for (const _originItem of bestCompatible.indexes) {
                    _originItem.onlyHere = false;
                    _originItem.isDifferent = false;
                }
            }
        } else if (!live) {
            for (const _originItem of design.indexes) {
                _originItem.onlyHere = false;
            }
            if (bestCompatible) {
                for (const _originItem of bestCompatible.indexes) {
                    _originItem.onlyHere = false;
                    _originItem.isDifferent = false;
                }
            }
        }
    }

    private configureButtons(uri: string, comparison: QuarantineTableComparisonResult) {
        let button: DTypeGoLiveActionButton;
        switch (comparison) {
            case QuarantineTableComparisonResult.EQUAL:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.equalMessage'),
                    design: {show: false, selected: false, caption: ''},
                    compatible: {show: false, selected: false, caption: ''},
                    migrate: {show: false, selected: false, caption: ''}
                };
                return button;
            case QuarantineTableComparisonResult.COMPATIBLE:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.compatibleMessage'),
                    design: {show: true, selected: false, caption: this.translocoService.translate('dbTables.buttons.useDesign'),
                        info: this.translocoService.translate('dbTables.buttons.compatibleDesignInfo')},
                    compatible: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.useBestCompatible'),
                        info: this.translocoService.translate('dbTables.buttons.compatibleCompatibleInfo')},
                    migrate: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.migrateData')}
                };
                return button;
            case QuarantineTableComparisonResult.FORWARD_COMPATIBLE:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.forwardCompatibleMessage'),
                    design: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.useDesign')},
                    compatible: {show: false, selected: false, caption: this.translocoService.translate('dbTables.buttons.useBestCompatible')},
                    migrate: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.migrateData')}
                };
                return button;
            case QuarantineTableComparisonResult.INCOMPATIBLE:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.incompatibleMessage'),
                    design: {show: true, selected: false, caption: this.translocoService.translate('dbTables.buttons.useDesign'),
                        info: this.translocoService.translate('dbTables.buttons.incompatibleDesignInfo')},
                    compatible: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.useBestIncompatible'),
                        info: this.translocoService.translate('dbTables.buttons.incompatibleCompatibleInfo')},
                        migrate: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.migrateData')}
                };
                return button;
            case QuarantineTableComparisonResult.MISSING_DESIGN:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.missingDesignMessage'),
                    design: {show: true, selected: true, caption: this.translocoService.translate('dbTables.buttons.useDelete')},
                    compatible: {show: true, selected: false, caption: this.translocoService.translate('dbTables.buttons.usePreserve')},
                    migrate: {show: false, selected: false, caption: ''}
                };
                return button;
            case QuarantineTableComparisonResult.MISSING_LIVE:
                button = {
                    uri: uri,
                    message: this.translocoService.translate('dbTables.buttons.missingLiveMessage'),
                    design: {show: false, selected: false, caption: ''},
                    compatible: {show: false, selected: false, caption: ''},
                    migrate: {show: false, selected: false, caption: ''}
                };
                return button;
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.translocoService.translate('unsavedChanges'),
            message: message ? message : this.translocoService.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.translocoService.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.translocoService.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private openConfirmDialogWithCountdown(title: string, message: string, countdownMessage: string) {
        const dialog = this.dialogsService.openConfirmWithCountdownDialog({
            title,
            message,
            countdownMessage,
            seconds: 10,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('revert'),
                }
            },
        });

        return new Promise<boolean>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
